/* The everyday timezone picker — STORY-013, over STORY-041's enabled set.
 *
 * A native `<select>` over one to five values, not ~420 flat options opening on
 * `Africa/Abidjan`. `design.md` prefers a real select where the behaviour
 * permits one, and with a handful of options it does — it is keyboard
 * accessible, screen-reader friendly and correct on mobile for free.
 *
 * ── the hard stop is deliberate ─────────────────────────────────────────────
 * Somebody who needs a zone that is not enabled cannot add it here. Enabling is
 * Owner-only and deliberate — STORY-041 — so the hint names where it is done
 * and the record being written is preserved. There is no inline enable.
 *
 * ── a disabled zone stays selected ──────────────────────────────────────────
 * A Venue whose zone was later turned off keeps it, still shows it, and is
 * still selected on it. Enablement governs the picker, never stored data — the
 * alternative silently blanks the field the moment somebody edits the name.
 */

import { zoneCity, zoneOffset } from '@eventuley/i18n';
import { useId } from 'react';
import { useTranslate } from '../../../shared/i18n/index.js';

export interface TimezoneSelectProps {
  readonly label: string;
  /** The IANA identifier, or `''` for "use the workspace's". */
  readonly value: string;
  readonly enabled: readonly string[];
  readonly onChange: (next: string) => void;
  /** Whether the reader may reach Settings to enable more. */
  readonly canManage: boolean;
}

export function TimezoneSelect({
  label,
  value,
  enabled,
  onChange,
  canManage,
}: TimezoneSelectProps) {
  const t = useTranslate();
  const id = useId();
  const hintId = `${id}-hint`;

  /* A value outside the enabled set is prepended rather than dropped. */
  const options = value === '' || enabled.includes(value) ? enabled : [value, ...enabled];

  /* Label and hint are siblings wired by id, not a wrapper label with the hint
   * inside it. A wrapper's accessible name is its whole text content, so the
   * hint would be read out as part of the field's name on every focus. */
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="field__control"
        value={value}
        aria-describedby={hintId}
        onChange={(changed) => onChange(changed.target.value)}
      >
        {/* Empty is a real choice, not a prompt: a Venue with no zone falls back
            to the workspace's, and so does an Event with no Venue. */}
        <option value="">{t('venues.timezone_inherited')}</option>
        {options.map((zone) => (
          <option key={zone} value={zone}>
            {enabled.includes(zone)
              ? `${zoneCity(zone)} · ${zoneOffset(zone)}`
              : t('venues.timezone_disabled', { city: zoneCity(zone) })}
          </option>
        ))}
      </select>
      <p className="field__hint" id={hintId}>
        {canManage ? t('venues.timezone_hint_owner') : t('venues.timezone_hint_member')}
      </p>
    </div>
  );
}
